  <template>
<div v-if="block?.data?.media" class="jigsaw-puzzle-container w-full h-96 min-h-80 relative">
  <div class="debug-info absolute top-0 left-0 bg-black text-white p-2 text-xs z-50">
    State: {{ this.state }} | Pieces: {{ this.polyPieces.length }} | Image: {{ this.imageLoaded ? 'Loaded' : 'Loading' }}
    <br>Container: {{ this.containerWidth }}x{{ this.containerHeight }} | Game: {{ this.gameWidth }}x{{ this.gameHeight }}
    <br>Scale: {{ this.scalex.toFixed(1) }}x{{ this.scaley.toFixed(1) }} | Shape: {{ getShapeName() }}
    <br>
    <button @click="switchShape" class="bg-blue-500 text-white px-2 py-1 rounded text-xs mt-1">
      Switch Shape ({{ getShapeName() }})
    </button>
  </div>
    <div 
      ref="puzzleContainer" 
      class="puzzle-container absolute w-[95%] h-[95%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-100 overflow-hidden"
      :class="{ 'cursor-pointer': !isCompleted, 'completed-state': isCompleted }"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @mousemove="handleMouseMove"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
      @touchmove="handleTouchMove"
    >
      <!-- Puzzle pieces will be rendered here -->
    </div>
  </div>
</template>
<script>
// Utility functions
const mhypot = Math.hypot
const mrandom = Math.random
const mmax = Math.max
const mmin = Math.min
const mround = Math.round
const mfloor = Math.floor
const msqrt = Math.sqrt
const mabs = Math.abs

function alea(min, max) {
  if (typeof max == 'undefined') return min * mrandom()
  return min + (max - min) * mrandom()
}

function intAlea(min, max) {
  if (typeof max == 'undefined') {
    max = min
    min = 0
  }
  return mfloor(min + (max - min) * mrandom())
}

function arrayShuffle(array) {
  let k1, temp
  for (let k = array.length - 1; k >= 1; --k) {
    k1 = intAlea(0, k + 1)
    temp = array[k]
    array[k] = array[k1]
    array[k1] = temp
  }
  return array
}

// Point class
class Point {
  constructor(x, y) {
    this.x = Number(x)
    this.y = Number(y)
  }
  
  copy() {
    return new Point(this.x, this.y)
  }
  
  distance(otherPoint) {
    return mhypot(this.x - otherPoint.x, this.y - otherPoint.y)
  }
}

// Segment class
class Segment {
  constructor(p1, p2) {
    this.p1 = new Point(p1.x, p1.y)
    this.p2 = new Point(p2.x, p2.y)
  }
  
  dx() {
    return this.p2.x - this.p1.x
  }
  
  dy() {
    return this.p2.y - this.p1.y
  }
  
  length() {
    return mhypot(this.dx(), this.dy())
  }
  
  pointOnRelative(coeff) {
    let dx = this.dx()
    let dy = this.dy()
    return new Point(this.p1.x + coeff * dx, this.p1.y + coeff * dy)
  }
}

// Side class
class Side {
  constructor() {
    this.type = ""
    this.points = []
  }
  
  reversed() {
    const ns = new Side()
    ns.type = this.type
    ns.points = this.points.slice().reverse()
    return ns
  }
  
  scale(puzzle) {
    const coefx = puzzle.scalex
    const coefy = puzzle.scaley
    this.scaledPoints = this.points.map(p => new Point(p.x * coefx, p.y * coefy))
  }
  
  drawPath(ctx, shiftx, shifty, withoutMoveTo) {
    if (!withoutMoveTo) {
      ctx.moveTo(this.scaledPoints[0].x + shiftx, this.scaledPoints[0].y + shifty)
    }
    if (this.type == "d") {
      ctx.lineTo(this.scaledPoints[1].x + shiftx, this.scaledPoints[1].y + shifty)
    } else {
      for (let k = 1; k < this.scaledPoints.length - 1; k += 3) {
        ctx.bezierCurveTo(
          this.scaledPoints[k].x + shiftx, this.scaledPoints[k].y + shifty,
          this.scaledPoints[k + 1].x + shiftx, this.scaledPoints[k + 1].y + shifty,
          this.scaledPoints[k + 2].x + shiftx, this.scaledPoints[k + 2].y + shifty
        )
      }
    }
  }
}

// Twist functions
function twist0(side, ca, cb) {
  // classic jigsaw puzzle
  const seg0 = new Segment(side.points[0], side.points[1])
  const dxh = seg0.dx()
  const dyh = seg0.dy()
  
  const seg1 = new Segment(ca, cb)
  const mid0 = seg0.pointOnRelative(0.5)
  const mid1 = seg1.pointOnRelative(0.5)
  
  const segMid = new Segment(mid0, mid1)
  const dxv = segMid.dx()
  const dyv = segMid.dy()
  
  const scalex = alea(0.8, 1)
  const scaley = alea(0.9, 1)
  const mid = alea(0.45, 0.55)
  
  const pa = pointAt(mid - 1 / 12 * scalex, 1 / 12 * scaley)
  const pb = pointAt(mid - 2 / 12 * scalex, 3 / 12 * scaley)
  const pc = pointAt(mid, 4 / 12 * scaley)
  const pd = pointAt(mid + 2 / 12 * scalex, 3 / 12 * scaley)
  const pe = pointAt(mid + 1 / 12 * scalex, 1 / 12 * scaley)
  
  side.points = [
    seg0.p1,
    new Point(seg0.p1.x + 5 / 12 * dxh * 0.52, seg0.p1.y + 5 / 12 * dyh * 0.52),
    new Point(pa.x - 1 / 12 * dxv * 0.72, pa.y - 1 / 12 * dyv * 0.72),
    pa,
    new Point(pa.x + 1 / 12 * dxv * 0.72, pa.y + 1 / 12 * dyv * 0.72),
    new Point(pb.x - 1 / 12 * dxv * 0.92, pb.y - 1 / 12 * dyv * 0.92),
    pb,
    new Point(pb.x + 1 / 12 * dxv * 0.52, pb.y + 1 / 12 * dyv * 0.52),
    new Point(pc.x - 2 / 12 * dxh * 0.40, pc.y - 2 / 12 * dyh * 0.40),
    pc,
    new Point(pc.x + 2 / 12 * dxh * 0.40, pc.y + 2 / 12 * dyh * 0.40),
    new Point(pd.x + 1 / 12 * dxv * 0.52, pd.y + 1 / 12 * dyv * 0.52),
    pd,
    new Point(pd.x - 1 / 12 * dxv * 0.92, pd.y - 1 / 12 * dyv * 0.92),
    new Point(pe.x + 1 / 12 * dxv * 0.72, pe.y + 1 / 12 * dyv * 0.72),
    pe,
    new Point(pe.x - 1 / 12 * dxv * 0.72, pe.y - 1 / 12 * dyv * 0.72),
    new Point(seg0.p2.x - 5 / 12 * dxh * 0.52, seg0.p2.y - 5 / 12 * dyh * 0.52),
    seg0.p2
  ]
  side.type = "z"
  
  function pointAt(coeffh, coeffv) {
    return new Point(seg0.p1.x + coeffh * dxh + coeffv * dxv, seg0.p1.y + coeffh * dyh + coeffv * dyv)
  }
}

function twist1(side, ca, cb) {
  // z-shape jigsaw puzzle
  const seg0 = new Segment(side.points[0], side.points[1])
  const dxh = seg0.dx()
  const dyh = seg0.dy()
  
  const seg1 = new Segment(ca, cb)
  const mid0 = seg0.pointOnRelative(0.5)
  const mid1 = seg1.pointOnRelative(0.5)
  
  const segMid = new Segment(mid0, mid1)
  const dxv = segMid.dx()
  const dyv = segMid.dy()
  
  const pa = pointAt(alea(0.3, 0.35), alea(-0.05, 0.05))
  const pb = pointAt(alea(0.45, 0.55), alea(0.2, 0.3))
  const pc = pointAt(alea(0.65, 0.78), alea(-0.05, 0.05))
  
  side.points = [
    seg0.p1, seg0.p1, pa, pa,
    pa, pb, pb,
    pb, pc, pc,
    pc, seg0.p2, seg0.p2
  ]
  side.type = "z"
  
  function pointAt(coeffh, coeffv) {
    return new Point(seg0.p1.x + coeffh * dxh + coeffv * dxv, seg0.p1.y + coeffh * dyh + coeffv * dyv)
  }
}

function twist2(side, ca, cb) {
  // curve jigsaw puzzle
  const seg0 = new Segment(side.points[0], side.points[1])
  const dxh = seg0.dx()
  const dyh = seg0.dy()
  
  const seg1 = new Segment(ca, cb)
  const mid0 = seg0.pointOnRelative(0.5)
  const mid1 = seg1.pointOnRelative(0.5)
  
  const segMid = new Segment(mid0, mid1)
  const dxv = segMid.dx()
  const dyv = segMid.dy()
  
  const hmid = alea(0.45, 0.55)
  const vmid = alea(0.4, 0.5)
  const pc = pointAt(hmid, vmid)
  let sega = new Segment(seg0.p1, pc)
  
  const pb = sega.pointOnRelative(2 / 3)
  sega = new Segment(seg0.p2, pc)
  const pd = sega.pointOnRelative(2 / 3)
  
  side.points = [seg0.p1, pb, pd, seg0.p2]
  side.type = "z"
  
  function pointAt(coeffh, coeffv) {
    return new Point(seg0.p1.x + coeffh * dxh + coeffv * dxv, seg0.p1.y + coeffh * dyh + coeffv * dyv)
  }
}

function twist3(side, ca, cb) {
  // straight line jigsaw puzzle
  side.points = [side.points[0], side.points[1]]
}

function twist4(side, ca, cb) {
  // Torn paper effect based on twist1 layout, but with jagged bezier controls
  // Keep endpoints aligned to the straight side to avoid sloped outer edges and gaps
  const seg0 = new Segment(side.points[0], side.points[1])
  const dxh = seg0.dx()
  const dyh = seg0.dy()

  const seg1 = new Segment(ca, cb)
  const mid0 = seg0.pointOnRelative(0.5)
  const mid1 = seg1.pointOnRelative(0.5)

  const segMid = new Segment(mid0, mid1)
  const dxv = segMid.dx()
  const dyv = segMid.dy()

  // Helper to place a point at relative coordinates along main and mid directions
  function pointAt(coeffh, coeffv) {
    return new Point(seg0.p1.x + coeffh * dxh + coeffv * dxv, seg0.p1.y + coeffh * dyh + coeffv * dyv)
  }

  // Perpendicular unit vector to the main edge (for torn perturbations)
  const len = Math.max(1e-6, Math.hypot(dxh, dyh))
  const nx = -dyh / len
  const ny = dxh / len

  // Build a serrated edge with triangular-like teeth: use a sharp peak per tooth by
  // splitting each interval into two beziers that meet at a peak, with control points
  // pinned to the peak to create a corner (less round, more like torn triangles).
  const p1 = seg0.p1
  const p2 = seg0.p2

  // Number of teeth along the edge (denser for finer serration)
  const teeth = intAlea(3, 6)

  // Base amplitude in grid units (lower height to make teeth less tall)
  const baseAmp = alea(0.006, 0.018)

  // Helper to get point along the main segment
  function pointOn(t) {
    return new Point(p1.x + dxh * t, p1.y + dyh * t)
  }

  // Control handle distances along the sub-segments (short for sharp look)
  const hIn = 0.18
  const hOut = 0.82

  const pts = []
  pts.push(p1)

  for (let i = 0; i < teeth; i++) {
    const t0 = i / teeth
    const t1 = (i + 1) / teeth
    const a0 = pointOn(t0)
    const a1 = pointOn(t1)
    const sign = (i % 2 === 0) ? 1 : -1
    const amp = baseAmp * alea(0.8, 1.4)

    // Peak at midpoint with perpendicular offset
    const tm = (t0 + t1) * 0.5
    const pm = pointOn(tm)
    const peak = new Point(pm.x + nx * sign * amp, pm.y + ny * sign * amp)

    // First half: a0 -> peak (sharp at peak by setting second control to peak)
    const c1a = new Point(
      a0.x + (peak.x - a0.x) * hIn,
      a0.y + (peak.y - a0.y) * hIn
    )
    const c2a = peak // sharp corner at peak

    pts.push(c1a, c2a, peak)

    // Second half: peak -> a1 (sharp at peak by setting first control to peak)
    const c1b = peak // sharp corner at peak
    const c2b = new Point(
      peak.x + (a1.x - peak.x) * hOut,
      peak.y + (a1.y - peak.y) * hOut
    )

    pts.push(c1b, c2b, a1)
  }

  side.points = pts
  side.type = "z"
}

// Piece class
class Piece {
  constructor(kx, ky) {
    this.ts = new Side()
    this.rs = new Side()
    this.bs = new Side()
    this.ls = new Side()
    this.kx = kx
    this.ky = ky
  }
  
  scale(puzzle) {
    this.ts.scale(puzzle)
    this.rs.scale(puzzle)
    this.bs.scale(puzzle)
    this.ls.scale(puzzle)
  }
}

// PolyPiece class
class PolyPiece {
  constructor(initialPiece, puzzle) {
    this.pckxmin = initialPiece.kx
    this.pckxmax = initialPiece.kx + 1
    this.pckymin = initialPiece.ky
    this.pckymax = initialPiece.ky + 1
    this.pieces = [initialPiece]
    this.puzzle = puzzle
    this.listLoops()
    
    this.canvas = document.createElement('CANVAS')
    puzzle.$refs.puzzleContainer.appendChild(this.canvas)
    this.canvas.classList.add('polypiece')
    this.ctx = this.canvas.getContext("2d")
  }
  
  listLoops() {
    const that = this
    function edgeIsCommon(kx, ky, edge) {
      let k
      switch (edge) {
        case 0: ky--; break
        case 1: kx++; break
        case 2: ky++; break
        case 3: kx--; break
      }
      for (k = 0; k < that.pieces.length; k++) {
        if (kx == that.pieces[k].kx && ky == that.pieces[k].ky) return true
      }
      return false
    }
    
    function edgeIsInTbEdges(kx, ky, edge) {
      let k
      for (k = 0; k < tbEdges.length; k++) {
        if (kx == tbEdges[k].kx && ky == tbEdges[k].ky && edge == tbEdges[k].edge) return k
      }
      return false
    }
    
    let tbLoops = []
    let tbEdges = []
    let k, kEdge, lp, currEdge, tries, edgeNumber, potNext
    
    let tbTries = [
      [{ dkx: 0, dky: 0, edge: 1 }, { dkx: 1, dky: 0, edge: 0 }, { dkx: 1, dky: -1, edge: 3 }],
      [{ dkx: 0, dky: 0, edge: 2 }, { dkx: 0, dky: 1, edge: 1 }, { dkx: 1, dky: 1, edge: 0 }],
      [{ dkx: 0, dky: 0, edge: 3 }, { dkx: -1, dky: 0, edge: 2 }, { dkx: -1, dky: 1, edge: 1 }],
      [{ dkx: 0, dky: 0, edge: 0 }, { dkx: 0, dky: -1, edge: 3 }, { dkx: -1, dky: -1, edge: 2 }]
    ]
    
    for (k = 0; k < this.pieces.length; k++) {
      for (kEdge = 0; kEdge < 4; kEdge++) {
        if (!edgeIsCommon(this.pieces[k].kx, this.pieces[k].ky, kEdge))
          tbEdges.push({ kx: this.pieces[k].kx, ky: this.pieces[k].ky, edge: kEdge, kp: k })
      }
    }
    
    while (tbEdges.length > 0) {
      lp = []
      currEdge = tbEdges[0]
      lp.push(currEdge)
      tbEdges.splice(0, 1)
      do {
        for (tries = 0; tries < 3; tries++) {
          potNext = tbTries[currEdge.edge][tries]
          edgeNumber = edgeIsInTbEdges(currEdge.kx + potNext.dkx, currEdge.ky + potNext.dky, potNext.edge)
          if (edgeNumber === false) continue
          currEdge = tbEdges[edgeNumber]
          lp.push(currEdge)
          tbEdges.splice(edgeNumber, 1)
          break
        }
        if (edgeNumber === false) break
      } while (1)
      tbLoops.push(lp)
    }
    
    this.tbLoops = tbLoops.map(loop => loop.map(edge => {
      let cell = this.pieces[edge.kp]
      if (edge.edge == 0) return cell.ts
      if (edge.edge == 1) return cell.rs
      if (edge.edge == 2) return cell.bs
      return cell.ls
    }))
  }
  
  drawPath(ctx, shiftx, shifty) {
    this.tbLoops.forEach(loop => {
      let without = false
      loop.forEach(side => {
        side.drawPath(ctx, shiftx, shifty, without)
        without = true
      })
      ctx.closePath()
    })
  }
  
  drawImage() {
    const puzzle = this.puzzle
    
    // Ensure puzzle canvas is ready
    if (!puzzle.gameCanvas || puzzle.gameCanvas.width <= 0 || puzzle.gameCanvas.height <= 0) {
      console.log('Game canvas not ready for drawing piece')
      return
    }
    
    this.nx = this.pckxmax - this.pckxmin + 1
    this.ny = this.pckymax - this.pckymin + 1
    this.canvas.width = this.nx * puzzle.scalex
    this.canvas.height = this.ny * puzzle.scaley
    this.canvas.style.position = 'absolute'
    
    this.offsx = (this.pckxmin - 0.5) * puzzle.scalex
    this.offsy = (this.pckymin - 0.5) * puzzle.scaley
    
    this.path = new Path2D()
    this.drawPath(this.path, -this.offsx, -this.offsy)
    
    this.ctx.fillStyle = 'none'
    this.ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
    this.ctx.shadowBlur = 4
    this.ctx.shadowOffsetX = 4
    this.ctx.shadowOffsetY = 4
    this.ctx.fill(this.path)
    this.ctx.shadowColor = 'rgba(0, 0, 0, 0)'
    
    this.pieces.forEach((pp, kk) => {
      this.ctx.save()
      
      const path = new Path2D()
      const shiftx = -this.offsx
      const shifty = -this.offsy
      pp.ts.drawPath(path, shiftx, shifty, false)
      pp.rs.drawPath(path, shiftx, shifty, true)
      pp.bs.drawPath(path, shiftx, shifty, true)
      pp.ls.drawPath(path, shiftx, shifty, true)
      path.closePath()
      
      this.ctx.clip(path)
      const srcx = pp.kx ? ((pp.kx - 0.5) * puzzle.scalex) : 0
      const srcy = pp.ky ? ((pp.ky - 0.5) * puzzle.scaley) : 0
      
      const destx = (pp.kx ? 0 : puzzle.scalex / 2) + (pp.kx - this.pckxmin) * puzzle.scalex
      const desty = (pp.ky ? 0 : puzzle.scaley / 2) + (pp.ky - this.pckymin) * puzzle.scaley
      
      let w = 2 * puzzle.scalex
      let h = 2 * puzzle.scaley
      if (srcx + w > puzzle.gameCanvas.width) w = puzzle.gameCanvas.width - srcx
      if (srcy + h > puzzle.gameCanvas.height) h = puzzle.gameCanvas.height - srcy
      
      this.ctx.drawImage(puzzle.gameCanvas, srcx, srcy, w, h, destx, desty, w, h)
      
      this.ctx.translate(puzzle.embossThickness / 2, -puzzle.embossThickness / 2)
      this.ctx.lineWidth = puzzle.embossThickness
      this.ctx.strokeStyle = "rgba(0, 0, 0, 0.35)"
      this.ctx.stroke(path)
      
      this.ctx.translate(-puzzle.embossThickness, puzzle.embossThickness)
      this.ctx.strokeStyle = "rgba(255, 255, 255, 0.35)"
      this.ctx.stroke(path)
      this.ctx.restore()
    })
  }
  
  moveTo(x, y) {
    this.x = x
    this.y = y
    this.canvas.style.left = x + 'px'
    this.canvas.style.top = y + 'px'
  }
  
  moveToInitialPlace() {
    const puzzle = this.puzzle
    this.moveTo(
      puzzle.offsx + (this.pckxmin - 0.5) * puzzle.scalex,
      puzzle.offsy + (this.pckymin - 0.5) * puzzle.scaley
    )
  }
  
  ifNear(otherPoly) {
    let puzzle = this.puzzle
    let x = this.x - puzzle.scalex * this.pckxmin
    let y = this.y - puzzle.scaley * this.pckymin
    let ppx = otherPoly.x - puzzle.scalex * otherPoly.pckxmin
    let ppy = otherPoly.y - puzzle.scaley * otherPoly.pckymin
    if (mhypot(x - ppx, y - ppy) >= puzzle.dConnect) return false
    
    for (let k = this.pieces.length - 1; k >= 0; --k) {
      let p1 = this.pieces[k]
      for (let ko = otherPoly.pieces.length - 1; ko >= 0; --ko) {
        let p2 = otherPoly.pieces[ko]
        if (p1.kx == p2.kx && mabs(p1.ky - p2.ky) == 1) return true
        if (p1.ky == p2.ky && mabs(p1.kx - p2.kx) == 1) return true
      }
    }
    return false
  }
  
  merge(otherPoly) {
    const orgpckxmin = this.pckxmin
    const orgpckymin = this.pckymin
    
    const kOther = this.puzzle.polyPieces.indexOf(otherPoly)
    this.puzzle.polyPieces.splice(kOther, 1)
    this.puzzle.$refs.puzzleContainer.removeChild(otherPoly.canvas)
    
    for (let k = 0; k < otherPoly.pieces.length; ++k) {
      this.pieces.push(otherPoly.pieces[k])
      if (otherPoly.pieces[k].kx < this.pckxmin) this.pckxmin = otherPoly.pieces[k].kx
      if (otherPoly.pieces[k].kx + 1 > this.pckxmax) this.pckxmax = otherPoly.pieces[k].kx + 1
      if (otherPoly.pieces[k].ky < this.pckymin) this.pckymin = otherPoly.pieces[k].ky
      if (otherPoly.pieces[k].ky + 1 > this.pckymax) this.pckymax = otherPoly.pieces[k].ky + 1
    }
    
    this.pieces.sort(function (p1, p2) {
      if (p1.ky < p2.ky) return -1
      if (p1.ky > p2.ky) return 1
      if (p1.kx < p2.kx) return -1
      if (p1.kx > p2.kx) return 1
      return 0
    })
    
    this.listLoops()
    this.drawImage()
    this.moveTo(
      this.x + this.puzzle.scalex * (this.pckxmin - orgpckxmin),
      this.y + this.puzzle.scaley * (this.pckymin - orgpckymin)
    )
    this.puzzle.evaluateZIndex()
  }
}

export default {
  props: {
    block: {
      type: Object,
      required: true
    },
    isCompleted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      puzzle: null,
      gameCanvas: null,
      polyPieces: [],
      events: [],
      state: 0,
      moving: null,
      tmpImage: null,
      imageLoaded: false,
      srcImage: null,
      containerWidth: 0,
      containerHeight: 0,
      gameWidth: 0,
      gameHeight: 0,
      scalex: 0,
      scaley: 0,
      offsx: 0,
      offsy: 0,
      dConnect: 0,
      embossThickness: 0,
      nx: 0,
      ny: 0,
      pieces: [],
      zIndexSup: 0,
      // Available twist functions for different piece shapes
      twistFunctions: [twist0, twist1, twist2, twist3, twist4],
      currentTwist: 4, // Default to torn paper effect (twist4)
      animationId: null, // Track animation frame ID
      initialized: false
    }
  },
  mounted() {
    this.startAnimation()
    // Add window resize listener
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    this.cleanup()
  },
  watch: {
    block: {
      handler(newBlock) {
        if (newBlock?.data?.media) {
          // Use nextTick to ensure DOM is ready
          this.$nextTick(() => {
            this.initializePuzzle();
          });
        }
      },
      immediate: true
    },
    isCompleted: {
      handler(newValue) {
        if (newValue && this.state !== 9999) {
          // If puzzle is completed, skip to completed state
          console.log('Puzzle is already completed, showing completed state')
          this.showCompletedState()
        }
      },
      immediate: true
    }
  },
  methods: {
    initializePuzzle() {

      if (!this.block?.data?.media?.preview_url) {
        console.log('No media preview url found')
        return
      }

      if (!this.$refs.puzzleContainer) {
        console.log('Puzzle container not ready yet')
        return
      }

      // If puzzle is already completed, show completed state instead
      if (this.isCompleted) {
        console.log('Puzzle is already completed, showing completed state')
        this.showCompletedState()
        return
      }

      // Clean up any existing puzzle
      this.cleanupPuzzle()

      // Prevent multiple initializations
      if (this.gameCanvas) {
        console.log('Puzzle already initialized')
        return
      }

      this.containerWidth = this.$refs.puzzleContainer.offsetWidth
      this.containerHeight = this.$refs.puzzleContainer.offsetHeight
      
      // Fallback to window dimensions if container has no height
      if (this.containerHeight <= 0) {
        console.log('Container has no height, using fallback dimensions')
        this.containerHeight = Math.max(300, window.innerHeight * 0.4) // 40% of window height, minimum 300px
      }
      
      // Ensure we have valid dimensions
      if (this.containerWidth <= 0 || this.containerHeight <= 0) {
        console.log('Invalid container dimensions:', this.containerWidth, this.containerHeight)
        return
      }
      
      // Create game canvas
      this.gameCanvas = document.createElement('canvas')
      this.gameCanvas.classList.add('gameCanvas')
      this.gameCanvas.style.zIndex = 500
      this.$refs.puzzleContainer.appendChild(this.gameCanvas)
      
      // Create source image (with robust load/error handling)
      this.srcImage = new Image()
      // Allow drawing to canvas when served with CORS
      this.srcImage.crossOrigin = 'anonymous'

      // Bind handlers so we can remove them later
      this._onImgLoad = () => {
        console.log('Image loaded successfully:', this.srcImage?.src, this.srcImage?.naturalWidth, this.srcImage?.naturalHeight)
        this.imageLoaded = true
        // Nudge state machine if it was waiting
        this.events.push({ event: 'srcImageLoaded' })
        console.log('Pushed srcImageLoaded event, current state:', this.state)
      }
      this._onImgError = (e) => {
        console.error('Image failed to load:', this.srcImage?.src, e)
        this.imageLoaded = false
      }
      this.srcImage.addEventListener('load', this._onImgLoad)
      this.srcImage.addEventListener('error', this._onImgError)
      // Mark as initialized so the state machine can proceed
      this.initialized = true
      
      // Load initial image if available
      if (this.block?.data?.media?.preview_url) {
        const url = this.block.data.media.preview_url
        console.log('Setting image src to:', url)
        this.srcImage.src = url
        // If cached, load may have already completed
        if (this.srcImage.complete && this.srcImage.naturalWidth > 0) {
          console.log('Image was already cached, triggering load event')
          this._onImgLoad()
        } else {
          // Add a timeout fallback in case load event doesn't fire
          setTimeout(() => {
            if (this.srcImage.complete && this.srcImage.naturalWidth > 0 && !this.imageLoaded) {
              console.log('Image load timeout fallback triggered')
              this._onImgLoad()
            }
          }, 2000)
        }
      }
    },
    
    startAnimation() {
      if (this.animationId) {
        console.log('Jigsaw Puzzle: Animation already running, stopping previous one')
        this.stopAnimation()
      }
      
      const animate = () => {
        this.animationId = requestAnimationFrame(animate)
     //   console.log('Jigsaw Puzzle: Animation frame requested, animationId:', this.animationId)
        this.processEvents()
      }
      animate()
    },
    
    stopAnimation() {
      if (this.animationId) {
        console.log('Jigsaw Puzzle: Stopping animation loop')
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }
    },
    
    processEvents() {
      let event
      if (this.events.length) event = this.events.shift()
      
      if (event && event.event == "reset") this.state = 0
      if (event && event.event == "srcImageLoaded") {
        // Only allow srcImageLoaded to reset the state during initialization phases
        if (this.state === 10 || this.state === 0) {
          if (this.state === 10) {
            console.log('Received srcImageLoaded event while in state 10, proceeding')
          }
          console.log('srcImageLoaded event received, resetting state to 0')
          this.state = 0
        } else {
          // Ignore late srcImageLoaded events once the puzzle is already running
        }
      }
      
      if (event) {    
        console.log(`Processing state ${this.state}, event:`, event)
      }
      
      switch (this.state) {
        case 0:
          // Wait until initializePuzzle has created required resources
          if (!this.initialized) {
            return
          }
          console.log('State 0: Initializing')
          this.state = 10
          break
          
        case 10:
          // If not yet initialized or no image element, wait silently
          if (!this.initialized || !this.srcImage) {
            return
          }
          // Check if image is actually loaded (fallback check)
          if (this.srcImage && this.srcImage.complete && this.srcImage.naturalWidth > 0 && !this.imageLoaded) {
            console.log('State 10: Image appears loaded but flag not set, fixing...')
            this.imageLoaded = true
          }


          if (!this.imageLoaded) {
            // throttle logs: only print every ~30 frames
            if (!this._waitLogCounter || this._waitLogCounter++ % 30 === 0) {
              console.log('State 10: Waiting for image to load', this.srcImage?.src)
              console.log('Image complete:', this.srcImage?.complete, 'naturalWidth:', this.srcImage?.naturalWidth)
            }
            return
          }
          this._waitLogCounter = 0
          console.log('State 10: Image loaded, displaying initial image')
          this.displayInitialImage()
          this.nbPieces = this.block.data.amount ?? 8
          this.state = 20
          break
          
        case 20:
          console.log('State 20: Creating puzzle')
          this.createPuzzle()
          this.scalePuzzle()
          this.drawPieces()
          this.state = 25
          break
          
        case 25:
          console.log('State 25: Hiding game canvas and original image, adding moving class')
          this.gameCanvas.style.display = "none"
          // Hide all initial images to avoid duplicates being visible
          if (this.$refs.puzzleContainer) {
            const imgs = this.$refs.puzzleContainer.querySelectorAll('img.__initial-image__')
            imgs.forEach(img => { img.style.display = "none" })
          } else if (this.tmpImage && this.tmpImage.parentNode) {
            // Fallback: hide single reference if query is unavailable
            this.tmpImage.style.display = "none"
          }
          this.polyPieces.forEach(pp => {
            pp.canvas.classList.add("moving")
          })
          this.state = 30
          break
          
        case 30:
          console.log('State 30: Optimizing initial positions')
          this.optimInitial()
          setTimeout(() => {
            console.log('State 30: Timeout finished, pushing finished event')
            this.events.push({ event: "finished" })
          }, 1200)
          this.state = 35
          break
          
        case 35:
          if (!event || event.event !== "finished") {
            console.log('State 35: Waiting for finished event, current event:', event)
            return
          }
          console.log('State 35: Finished event received, removing moving class')
          this.polyPieces.forEach(pp => {
            pp.canvas.classList.remove("moving")
          })
          this.state = 50
          break
          
        case 50:
          if (!event) return
          if (event.event !== "touch") return
          console.log('State 50: Touch event received')
          this.handleTouch(event)
          break
          
        case 55:
          if (!event) return
          console.log('State 55: Move event received')
          this.handleMove(event)
          break
          
        case 60:
          console.log('State 60: Win condition')
          this.handleWin()
          this.state = 9999
          break
          
        case 9999:
          break
      }
    },
    
    displayInitialImage() {
      // Reuse existing initial image if present, otherwise create one
      if (!this.tmpImage || !this.tmpImage.isConnected) {
        const existing = this.$refs.puzzleContainer?.querySelector('img.__initial-image__')
        if (existing) {
          this.tmpImage = existing
        } else {
          this.tmpImage = document.createElement("img")
          this.tmpImage.classList.add("__initial-image__")
          this.tmpImage.src = this.srcImage.src
          this.$refs.puzzleContainer.appendChild(this.tmpImage)
        }
      }

      // Use source image dimensions if available
      const naturalWidth = this.srcImage?.naturalWidth || null
      const naturalHeight = this.srcImage?.naturalHeight || null
      
      // Fit and style (idempotent)
      this.fitImage(this.tmpImage, this.containerWidth * 0.95, this.containerHeight * 0.95, naturalWidth, naturalHeight)
      this.tmpImage.style.boxShadow = "4px 4px 4px rgba(0, 0, 0, 0.5)"
      this.tmpImage.style.zIndex = "100" // Above other elements initially
      this.tmpImage.style.display = "block"
    },
    
    createPuzzle() {
      // Ensure image is loaded before creating puzzle
      if (!this.srcImage || !this.srcImage.naturalWidth || !this.srcImage.naturalHeight) {
        console.log('Cannot create puzzle: image not loaded')
        return
      }
      
      this.computenxAndny()
      console.log(`Creating puzzle with shape: ${this.getShapeName()}`)
      this.defineShapes({ coeffDecentr: 0.12, twistf: this.twistFunctions[this.currentTwist] })
      
      this.polyPieces = []
      this.pieces.forEach(row => row.forEach(piece => {
        this.polyPieces.push(new PolyPiece(piece, this))
      }))
      
      arrayShuffle(this.polyPieces)
      this.evaluateZIndex()
    },
    
    computenxAndny() {
      let width = this.srcImage.naturalWidth
      let height = this.srcImage.naturalHeight
      let npieces = this.nbPieces
      let err, errmin = 1e9
      let ncv, nch
      
      let nHPieces = mround(msqrt(npieces * width / height))
      let nVPieces = mround(npieces / nHPieces)
      
      for (let ky = 0; ky < 5; ky++) {
        ncv = nVPieces + ky - 2
        for (let kx = 0; kx < 5; kx++) {
          nch = nHPieces + kx - 2
          
          // Ensure minimum dimensions of 1x1 to prevent zero pieces
          if (nch < 1) nch = 1
          if (ncv < 1) ncv = 1
          
          err = nch * height / ncv / width
          err = (err + 1 / err) - 2
          err += mabs(1 - nch * ncv / npieces)
          
          if (err < errmin) {
            errmin = err
            this.nx = nch
            this.ny = ncv
          }
        }
      }
      
      // Final safety check to ensure we have at least 1x1 grid
      if (this.nx < 1) this.nx = 1
      if (this.ny < 1) this.ny = 1
      
      console.log(`Grid calculated: ${this.nx}x${this.ny} = ${this.nx * this.ny} pieces (target: ${npieces})`)
    },
    
    defineShapes(shapeDesc) {
      let { coeffDecentr, twistf } = shapeDesc
      
      const corners = []
      const nx = this.nx, ny = this.ny
      
      for (let ky = 0; ky <= ny; ++ky) {
        corners[ky] = []
        for (let kx = 0; kx <= nx; ++kx) {
          corners[ky][kx] = new Point(
            kx + alea(-coeffDecentr, coeffDecentr),
            ky + alea(-coeffDecentr, coeffDecentr)
          )
          if (kx == 0) corners[ky][kx].x = 0
          if (kx == nx) corners[ky][kx].x = nx
          if (ky == 0) corners[ky][kx].y = 0
          if (ky == ny) corners[ky][kx].y = ny
        }
      }
      
      this.pieces = []
      for (let ky = 0; ky < ny; ++ky) {
        this.pieces[ky] = []
        for (let kx = 0; kx < nx; ++kx) {
          const np = new Piece(kx, ky)
          
          if (ky == 0) {
            np.ts.points = [corners[ky][kx], corners[ky][kx + 1]]
            np.ts.type = "d"
          } else {
            np.ts = this.pieces[ky - 1][kx].bs.reversed()
          }
          
          np.rs.points = [corners[ky][kx + 1], corners[ky + 1][kx + 1]]
          np.rs.type = "d"
          if (kx < nx - 1) {
            if (intAlea(2)) {
              twistf(np.rs, corners[ky][kx], corners[ky + 1][kx])
            } else {
              twistf(np.rs, corners[ky][kx + 2], corners[ky + 1][kx + 2])
            }
          }
          
          if (kx == 0) {
            np.ls.points = [corners[ky + 1][kx], corners[ky][kx]]
            np.ls.type = "d"
          } else {
            np.ls = this.pieces[ky][kx - 1].rs.reversed()
          }
          
          np.bs.points = [corners[ky + 1][kx + 1], corners[ky + 1][kx]]
          np.bs.type = "d"
          if (ky < ny - 1) {
            if (intAlea(2)) {
              twistf(np.bs, corners[ky][kx + 1], corners[ky][kx])
            } else {
              twistf(np.bs, corners[ky + 2][kx + 1], corners[ky + 2][kx])
            }
          }
          
          this.pieces[ky][kx] = np
        }
      }
    },
    
    scalePuzzle() {
      // Ensure image is loaded and has valid dimensions
      if (!this.srcImage || !this.srcImage.naturalWidth || !this.srcImage.naturalHeight) {
        console.log('Image not loaded or has invalid dimensions')
        return
      }
      
      const maxWidth = 0.95 * this.containerWidth
      const maxHeight = 0.95 * this.containerHeight
      
      this.gameHeight = maxHeight
      this.gameWidth = this.gameHeight * this.srcImage.naturalWidth / this.srcImage.naturalHeight
      
      if (this.gameWidth > maxWidth) {
        this.gameWidth = maxWidth
        this.gameHeight = this.gameWidth * this.srcImage.naturalHeight / this.srcImage.naturalWidth
      }
      
      // Ensure minimum dimensions
      this.gameWidth = Math.max(this.gameWidth, 100)
      this.gameHeight = Math.max(this.gameHeight, 100)
      
      this.gameCanvas.width = this.gameWidth
      this.gameCanvas.height = this.gameHeight
      
      // Only draw if canvas has valid dimensions
      if (this.gameCanvas.width > 0 && this.gameCanvas.height > 0) {
        const gameCtx = this.gameCanvas.getContext("2d")
        gameCtx.drawImage(this.srcImage, 0, 0, this.gameWidth, this.gameHeight)
      } else {
        console.log('Canvas has invalid dimensions:', this.gameCanvas.width, this.gameCanvas.height)
        return
      }
      
      this.scalex = this.gameWidth / this.nx
      this.scaley = this.gameHeight / this.ny
      
      console.log(`Scaling: gameWidth=${this.gameWidth}, gameHeight=${this.gameHeight}`)
      console.log(`Grid: nx=${this.nx}, ny=${this.ny}`)
      console.log(`Piece size: scalex=${this.scalex}, scaley=${this.scaley}`)
      
      this.pieces.forEach(row => {
        row.forEach(piece => piece.scale(this))
      })
      
      this.offsx = (this.containerWidth - this.gameWidth) / 2
      this.offsy = (this.containerHeight - this.gameHeight) / 2
      
      this.dConnect = mmax(10, mmin(this.scalex, this.scaley) / 10)
      this.embossThickness = mmin(2 + this.scalex / 200 * (5 - 2), 5)
    },
    
    drawPieces() {
      // Ensure canvas is ready
      if (!this.gameCanvas || this.gameCanvas.width <= 0 || this.gameCanvas.height <= 0) {
        console.log('Canvas not ready for drawing pieces')
        return
      }
      
      this.polyPieces.forEach(pp => {
        pp.drawImage()
        pp.moveToInitialPlace()
      })
      this.gameCanvas.style.top = this.offsy + "px"
      this.gameCanvas.style.left = this.offsx + "px"
      this.gameCanvas.style.display = "block"
    },
    
    optimInitial() {
      console.log('optimInitial: Starting piece positioning')
      console.log('Container dimensions:', this.containerWidth, this.containerHeight)
      console.log('Game dimensions:', this.gameWidth, this.gameHeight)
      console.log('Scale factors:', this.scalex, this.scaley)
      console.log('Number of pieces:', this.polyPieces.length)
      
      // Use the entire container area for piece distribution
      const minx = 0
      const miny = 0
      const maxx = this.containerWidth - this.scalex
      const maxy = this.containerHeight - this.scaley
      
      console.log('Positioning bounds:', { minx, miny, maxx, maxy })
      console.log('Container size:', this.containerWidth, this.containerHeight)
      console.log('Piece size:', this.scalex, this.scaley)
      
      // Always use the full container area for better distribution
      console.log('Using full container positioning')
      this.spreadInRectangle({
        x0: minx,
        x1: maxx,
        y0: miny,
        y1: maxy
      })
      
      console.log('optimInitial: Piece positioning completed')
    },
    
    spreadInRectangle(rect) {
      console.log('spreadInRectangle: Before limiting', rect)
      this.limitRectangle(rect)
      console.log('spreadInRectangle: After limiting', rect)
      
      this.polyPieces.forEach((pp, index) => {
        const x = alea(rect.x0, rect.x1)
        const y = alea(rect.y0, rect.y1)
        console.log(`Moving piece ${index} to (${x}, ${y})`)
        console.log(`Piece ${index} canvas before move:`, pp.canvas.style.left, pp.canvas.style.top)
        pp.moveTo(x, y)
        console.log(`Piece ${index} canvas after move:`, pp.canvas.style.left, pp.canvas.style.top)
      })
      console.log('spreadInRectangle: All pieces positioned')
    },
    
    limitRectangle(rect) {
      // Ensure pieces stay within visible container bounds
      console.log('limitRectangle: Before limiting', rect)
      console.log('Container dimensions:', this.containerWidth, this.containerHeight)
      console.log('Piece dimensions:', this.scalex, this.scaley)
      
      // Ensure we have valid bounds - use the full container area
      const maxX = Math.max(0, this.containerWidth - this.scalex)
      const maxY = Math.max(0, this.containerHeight - this.scaley)
      
      // Force pieces to be distributed across the entire visible area
      rect.x0 = 0
      rect.x1 = maxX
      rect.y0 = 0
      rect.y1 = maxY
      
      console.log('limitRectangle: After limiting', rect)
    },
    
    evaluateZIndex() {
      for (let k = this.polyPieces.length - 1; k > 0; --k) {
        if (this.polyPieces[k].pieces.length > this.polyPieces[k - 1].pieces.length) {
          [this.polyPieces[k], this.polyPieces[k - 1]] = [this.polyPieces[k - 1], this.polyPieces[k]]
        }
      }
      this.polyPieces.forEach((pp, k) => {
        pp.canvas.style.zIndex = k + 10
      })
      this.zIndexSup = this.polyPieces.length + 10
    },
    
    handleTouch(event) {
      console.log('handleTouch: Touch event at', event.position)
      console.log('Number of pieces:', this.polyPieces.length)
      console.log('Container bounds:', this.containerWidth, this.containerHeight)
      
      this.moving = {
        xMouseInit: event.position.x,
        yMouseInit: event.position.y
      }
      
      for (let k = this.polyPieces.length - 1; k >= 0; --k) {
        let pp = this.polyPieces[k]
        const relativeX = event.position.x - pp.x
        const relativeY = event.position.y - pp.y
        console.log(`Checking piece ${k} at (${pp.x}, ${pp.y}), relative (${relativeX}, ${relativeY})`)
        console.log(`Piece ${k} canvas position:`, pp.canvas.style.left, pp.canvas.style.top)
        console.log(`Piece ${k} canvas size:`, pp.canvas.width, pp.canvas.height)
        console.log(`Piece ${k} has path:`, !!pp.path)
        
        // Check if click is within piece bounds first
        if (relativeX >= 0 && relativeX <= pp.canvas.width && 
            relativeY >= 0 && relativeY <= pp.canvas.height) {
          console.log(`Piece ${k} is within bounds, checking path`)
          if (pp.ctx.isPointInPath(pp.path, relativeX, relativeY)) {
            console.log(`Piece ${k} selected!`)
            this.moving.pp = pp
            this.moving.ppXInit = pp.x
            this.moving.ppYInit = pp.y
            this.polyPieces.splice(k, 1)
            this.polyPieces.push(pp)
            pp.canvas.style.zIndex = this.zIndexSup
            this.state = 55
            return
          } else {
            console.log(`Piece ${k} path check failed`)
          }
        } else {
          console.log(`Piece ${k} outside bounds`)
        }
      }
      console.log('No piece selected')
    },
    
    constrainPiecePosition(pp, x, y) {
      // Get piece dimensions
      const pieceWidth = pp.canvas.width
      const pieceHeight = pp.canvas.height
      
      // Calculate minimum visible portion (30% of piece should remain visible for grabbing, minimum 20px)
      const minVisibleRatio = 0.3
      const minVisibleWidth = Math.max(pieceWidth * minVisibleRatio, 20)
      const minVisibleHeight = Math.max(pieceHeight * minVisibleRatio, 20)
      
      // Container bounds (relative to puzzle container, which starts at 0,0)
      const containerRight = this.containerWidth
      const containerBottom = this.containerHeight
      
      // Constrain X position:
      // - Right edge constraint: x + minVisibleWidth <= containerWidth
      //   => x <= containerWidth - minVisibleWidth
      // - Left edge constraint: x + (pieceWidth - minVisibleWidth) >= 0
      //   => x >= -(pieceWidth - minVisibleWidth)
      const maxX = containerRight - minVisibleWidth
      const minX = -(pieceWidth - minVisibleWidth)
      const constrainedX = Math.max(minX, Math.min(maxX, x))
      
      // Constrain Y position:
      // - Bottom edge constraint: y + minVisibleHeight <= containerHeight
      //   => y <= containerHeight - minVisibleHeight
      // - Top edge constraint: y + (pieceHeight - minVisibleHeight) >= 0
      //   => y >= -(pieceHeight - minVisibleHeight)
      const maxY = containerBottom - minVisibleHeight
      const minY = -(pieceHeight - minVisibleHeight)
      const constrainedY = Math.max(minY, Math.min(maxY, y))
      
      return { x: constrainedX, y: constrainedY }
    },
    
    handleMove(event) {
      switch (event.event) {
        case "move":
          const newX = event.position.x - this.moving.xMouseInit + this.moving.ppXInit
          const newY = event.position.y - this.moving.yMouseInit + this.moving.ppYInit
          const constrained = this.constrainPiecePosition(this.moving.pp, newX, newY)
          this.moving.pp.moveTo(constrained.x, constrained.y)
          break
        case "leave":
          let doneSomething
          do {
            doneSomething = false
            for (let k = this.polyPieces.length - 1; k >= 0; --k) {
              let pp = this.polyPieces[k]
              if (pp == this.moving.pp) continue
              if (this.moving.pp.ifNear(pp)) {
                if (pp.pieces.length > this.moving.pp.pieces.length) {
                  pp.merge(this.moving.pp)
                  this.moving.pp = pp
                } else {
                  this.moving.pp.merge(pp)
                }
                doneSomething = true
                break
              }
            }
          } while (doneSomething)
          this.evaluateZIndex()
          this.state = 50
          if (this.polyPieces.length === 1) this.state = 60
          return
      }
    },
    
    handleWin() {
      console.log('Puzzle completed!')
      this.$emit('complete')
      this.showCompletedState()
    },
    
    showCompletedState() {
      // Set state to completed
      this.state = 9999
      
      // Clean up any puzzle pieces if they exist
      this.polyPieces.forEach(pp => {
        if (pp.canvas && pp.canvas.parentNode) {
          pp.canvas.parentNode.removeChild(pp.canvas)
        }
      })
      this.polyPieces = []
      
      // Clean up game canvas if it exists
      if (this.gameCanvas && this.gameCanvas.parentNode) {
        this.gameCanvas.parentNode.removeChild(this.gameCanvas)
        this.gameCanvas = null
      }
      
      // Ensure we have the source image loaded
      if (!this.srcImage && this.block?.data?.media?.preview_url) {
        this.srcImage = new Image()
        this.srcImage.crossOrigin = 'anonymous'
        this.srcImage.src = this.block.data.media.preview_url
        
        this._onImgLoad = () => {
          this.imageLoaded = true
          this.displayCompletedImage()
        }
        this.srcImage.addEventListener('load', this._onImgLoad)
        
        // If already loaded (cached), trigger immediately
        if (this.srcImage.complete && this.srcImage.naturalWidth > 0) {
          this._onImgLoad()
        }
      } else if (this.srcImage && this.imageLoaded) {
        // Image already loaded, display it
        this.displayCompletedImage()
      } else if (this.srcImage && !this.imageLoaded) {
        // Wait for image to load
        if (!this._onImgLoad) {
          this._onImgLoad = () => {
            this.imageLoaded = true
            this.displayCompletedImage()
          }
          this.srcImage.addEventListener('load', this._onImgLoad)
        }
      }
    },
    
    displayCompletedImage() {
      if (!this.$refs.puzzleContainer) {
        console.log('Container not ready for displaying completed image')
        return
      }
      
      // Ensure we have a single initial image and show it at the same resolution
      if (!this.tmpImage || !this.tmpImage.isConnected) {
        const existing = this.$refs.puzzleContainer?.querySelector('img.__initial-image__')
        if (existing) {
          this.tmpImage = existing
        } else {
          this.tmpImage = document.createElement("img")
          this.tmpImage.classList.add("__initial-image__")
          this.tmpImage.src = this.srcImage.src
          this.$refs.puzzleContainer.appendChild(this.tmpImage)
        }
      }
      
      // Update container dimensions if needed
      if (this.containerWidth <= 0 || this.containerHeight <= 0) {
        this.containerWidth = this.$refs.puzzleContainer.offsetWidth
        this.containerHeight = this.$refs.puzzleContainer.offsetHeight
        
        if (this.containerHeight <= 0) {
          this.containerHeight = Math.max(300, window.innerHeight * 0.4)
        }
      }
      
      // Use source image dimensions if available (should be loaded already)
      const naturalWidth = this.srcImage?.naturalWidth || null
      const naturalHeight = this.srcImage?.naturalHeight || null
      
      // Fit image with proper aspect ratio
      this.fitImage(this.tmpImage, this.containerWidth * 0.95, this.containerHeight * 0.95, naturalWidth, naturalHeight)
      this.tmpImage.style.boxShadow = "4px 4px 4px rgba(0, 0, 0, 0.5)"
      this.tmpImage.style.display = "block"
      this.tmpImage.style.zIndex = "100"
    },
    
    fitImage(img, width, height, naturalWidth = null, naturalHeight = null) {
      // Use provided natural dimensions or get from image element
      let wn = naturalWidth || img.naturalWidth
      let hn = naturalHeight || img.naturalHeight
      
      // If natural dimensions not available, try to get from srcImage
      if ((!wn || !hn) && this.srcImage && this.srcImage.naturalWidth > 0) {
        wn = this.srcImage.naturalWidth
        hn = this.srcImage.naturalHeight
      }
      
      // If still no dimensions, wait for image to load
      if (!wn || !hn) {
        if (img.complete) {
          // Image should be loaded, but dimensions not available
          console.warn('Image natural dimensions not available, waiting for load')
          img.onload = () => {
            this.fitImage(img, width, height)
          }
        } else {
          // Wait for image to load
          img.onload = () => {
            this.fitImage(img, width, height)
          }
        }
        return
      }
      
      // Calculate dimensions maintaining aspect ratio
      let w = width
      let h = w * hn / wn
      
      // If calculated height exceeds available height, scale down based on height instead
      if (h > height) {
        h = height
        w = h * wn / hn
      }
      
      img.style.position = "absolute"
      img.style.width = w + "px"
      img.style.height = h + "px"
      img.style.objectFit = "contain" // Maintain aspect ratio, prevent stretching
      img.style.top = "50%"
      img.style.left = "50%"
      img.style.transform = "translate(-50%,-50%)"
    },
    
    relativeMouseCoordinates(event) {
      const br = this.$refs.puzzleContainer.getBoundingClientRect()
      return {
        x: event.clientX - br.x,
        y: event.clientY - br.y
      }
    },
    
    handleMouseDown(event) {
      if (this.isCompleted) return;
      event.preventDefault()
      const coords = this.relativeMouseCoordinates(event)
      console.log('Mouse down at:', coords)
      this.events.push({ event: 'touch', position: coords })
    },

    handleMouseUp(event) {
      if (this.isCompleted) return;
      event.preventDefault()
      this.events.push({ event: 'leave' })
    },

    handleMouseMove(event) {
      if (this.isCompleted) return;
      event.preventDefault()
      if (this.events.length && this.events[this.events.length - 1].event == "move") this.events.pop()
      this.events.push({ event: 'move', position: this.relativeMouseCoordinates(event) })
    },

    handleTouchStart(event) {
      if (this.isCompleted) return;
      event.preventDefault()
      if (event.touches.length != 1) return
      let ev = event.touches[0]
      this.events.push({ event: 'touch', position: this.relativeMouseCoordinates(ev) })
    },

    handleTouchEnd(event) {
      if (this.isCompleted) return;
      this.events.push({ event: 'leave' })
    },

    handleTouchMove(event) {
      if (this.isCompleted) return;
      event.preventDefault()
      if (event.touches.length != 1) return
      let ev = event.touches[0]
      if (this.events.length && this.events[this.events.length - 1].event == "move") this.events.pop()
      this.events.push({ event: 'move', position: this.relativeMouseCoordinates(ev) })
    },
    
    hasPiecesOutsideContainer() {
      // Check if any pieces are outside the container bounds
      for (const pp of this.polyPieces) {
        const pieceWidth = pp.canvas.width
        const pieceHeight = pp.canvas.height
        const x = pp.x
        const y = pp.y
        
        // Calculate minimum visible portion (30% of piece should remain visible for grabbing, minimum 20px)
        const minVisibleRatio = 0.3
        const minVisibleWidth = Math.max(pieceWidth * minVisibleRatio, 20)
        const minVisibleHeight = Math.max(pieceHeight * minVisibleRatio, 20)
        
        // Check if piece is outside container bounds
        // Piece is outside if less than minVisibleWidth/Height is visible
        const rightEdge = x + pieceWidth
        const bottomEdge = y + pieceHeight
        const visibleWidth = Math.min(rightEdge, this.containerWidth) - Math.max(x, 0)
        const visibleHeight = Math.min(bottomEdge, this.containerHeight) - Math.max(y, 0)
        
        if (visibleWidth < minVisibleWidth || visibleHeight < minVisibleHeight) {
          console.log(`Piece at (${x}, ${y}) is outside container bounds`)
          return true
        }
      }
      return false
    },
    
    handleResize() {
      // Recalculate dimensions on window resize
      if (this.$refs.puzzleContainer) {
        this.containerWidth = this.$refs.puzzleContainer.offsetWidth
        this.containerHeight = this.$refs.puzzleContainer.offsetHeight
        
        // Fallback to window dimensions if container has no height
        if (this.containerHeight <= 0) {
          this.containerHeight = Math.max(300, window.innerHeight * 0.4)
        }
        
        // Only resize if puzzle is initialized and in a playing state (state >= 25)
        if (this.gameCanvas && this.imageLoaded && this.state >= 25 && this.polyPieces.length > 0) {
          // Scale puzzle with new dimensions
          this.scalePuzzle()
          
          // Redraw pieces but keep their current positions (don't move to initial place)
          this.polyPieces.forEach(pp => {
            pp.drawImage()
            // Don't call moveToInitialPlace() - keep current position
          })
          
          // Check if any pieces are outside the container after resize
          if (this.hasPiecesOutsideContainer()) {
            console.log('Pieces are outside container after resize, reshuffling...')
            // Reshuffle pieces to fit within new container bounds
            this.optimInitial()
            // Remove moving class if present (we're not in initial animation state)
            this.polyPieces.forEach(pp => {
              pp.canvas.classList.remove("moving")
            })
          }
          
          // Update game canvas position
          this.gameCanvas.style.top = this.offsy + "px"
          this.gameCanvas.style.left = this.offsx + "px"
          
          // Game canvas should always be hidden in state >= 25 (after initial setup)
          this.gameCanvas.style.display = "none"
          
          // Ensure initial image stays hidden
          if (this.$refs.puzzleContainer) {
            const imgs = this.$refs.puzzleContainer.querySelectorAll('img.__initial-image__')
            imgs.forEach(img => { img.style.display = "none" })
          } else if (this.tmpImage && this.tmpImage.parentNode) {
            this.tmpImage.style.display = "none"
          }
        }
      }
    },
    
    cleanupPuzzle() {
      // Clean up existing puzzle elements
      if (this.tmpImage && this.tmpImage.parentNode) {
        this.tmpImage.parentNode.removeChild(this.tmpImage)
        this.tmpImage = null
      }
      
      if (this.gameCanvas && this.gameCanvas.parentNode) {
        this.gameCanvas.parentNode.removeChild(this.gameCanvas)
        this.gameCanvas = null
      }
      
      // Clean up puzzle pieces
      this.polyPieces.forEach(pp => {
        if (pp.canvas && pp.canvas.parentNode) {
          pp.canvas.parentNode.removeChild(pp.canvas)
        }
      })
      this.polyPieces = []
      
      // Reset state
      this.state = 0
      this.imageLoaded = false
      this.moving = null
      this.initialized = false
    },
    
    cleanup() {
      this.stopAnimation()
      if (this.srcImage) {
        if (this._onImgLoad) this.srcImage.removeEventListener('load', this._onImgLoad)
        if (this._onImgError) this.srcImage.removeEventListener('error', this._onImgError)
      }
      window.removeEventListener('resize', this.handleResize)
      this.cleanupPuzzle()
    },
    
    getShapeName() {
      const names = ['Classic', 'Simple', 'Curved', 'Straight', 'Torn Paper']
      return names[this.currentTwist] || 'Unknown'
    },
    
    switchShape() {
      // Cycle through available shapes
      this.currentTwist = (this.currentTwist + 1) % this.twistFunctions.length
      console.log(`Switched to shape: ${this.getShapeName()}`)
      
      // Recreate the puzzle with the new shape
      if (this.imageLoaded && this.polyPieces.length > 0) {
        this.cleanupPuzzle()
        this.$nextTick(() => {
          this.initializePuzzle()
        })
      }
    }
  }
}
</script>

<style scoped>
/* Tailwind classes handle most styling, these are for specific puzzle functionality */
.polypiece {
  display: block;
  overflow: hidden;
  position: absolute;
  cursor: pointer;
}

.moving {
  transition-property: top, left;
  transition-duration: 1s;
  transition-timing-function: linear;
}

.gameCanvas {
  display: none;
  overflow: hidden;
  position: absolute;
}

/* Ensure puzzle container has proper positioning context */
.puzzle-container {
  position: relative;
}

/* Disable pointer events when puzzle is completed to allow scrolling */
.puzzle-container.completed-state {
  pointer-events: none;
  cursor: default;
}

/* Debug info styling */
.debug-info {
  font-family: monospace;
  font-size: 10px;
  line-height: 1.2;
  display: none;
}
</style>